import {BufferSchema, uint8, int16, int32} from '@geckos.io/typed-array-buffer-schema';
import {MultiplayerModel, InvalidMultiplayerPacketError} from './schemaModel.js';
import {DAMAGE_PARTS} from './remoteDamage.js';

export const WOUND_FLAGS = {DECAL: 1, CARVE: 2, SPRAY: 4, LONG_SPRAY: 8};
export const WOUND_MODE = {LIVE: 0, HISTORY: 1};
export const WOUND_LIMITS = {history: 128, batch: 16, perSecond: 32, pendingSprays: 16, atlasSize: 512, atlasesPerFrame: 2, stampsPerFrame: 16};
export const WOUND_TEXTURES = ['Decal.png', 'skorch.png'];
const record = BufferSchema.schema('remoteWound', {
	id: int32, part: uint8, x: int16, y: int16, size: uint8,
	rotation: uint8, direction: uint8, texture: uint8, flags: uint8, burn: uint8,
});
export const woundModel = new MultiplayerModel(BufferSchema.schema('remoteWounds', {
	generation: int32, epoch: uint8, mode: uint8, wounds: [record],
}), {wounds: Array.from({length: WOUND_LIMITS.batch + 1}, (_, i) => i)});

const angleByte = value => Math.round((((value % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)) * 255 / (Math.PI * 2));
export const woundAngle = value => value * Math.PI * 2 / 255;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const decodeWounds = buffer => {
	const packet = woundModel.fromBuffer(buffer);
	if (packet.generation <= 0 || packet.epoch > 9 || packet.mode > WOUND_MODE.HISTORY || packet.wounds.some(w =>
		w.id <= 0 || w.part >= DAMAGE_PARTS.length || w.texture >= WOUND_TEXTURES.length || w.flags > 15 ||
		w.size === 0 || Math.abs(w.x) > 4096 || Math.abs(w.y) > 4096 ||
		((w.flags & WOUND_FLAGS.DECAL) && w.id > WOUND_LIMITS.history))) {
		throw new InvalidMultiplayerPacketError('Invalid wound event');
	}
	return packet;
};

// Store compact commands, never damaged images. The first 128 accepted stamps
// can be replayed for late joiners; further hits still produce transient spray.
export class LocalWounds {
	constructor() { this.generation = 0; this.clear(); }
	clear() {
		this.character = null;
		this.history = [];
		this.pending = [];
		this.sequence = 0;
		this.sentEpoch = -1;
		this.tokens = WOUND_LIMITS.batch;
		this.time = 0;
	}
	adopt(character) {
		if (!character || this.character === character) return;
		this.clear();
		this.character = character;
		this.generation++;
	}
	record(character, body, x, y, texture, carving, size = 1, rotation = 0, burn = 0, now = performance.now(), longSpray = false) {
		const key = body?.mySprite?.data?.refName;
		if (!character || !body || typeof key !== 'string' || character.lookupObject?.[key] !== body) return;
		const part = DAMAGE_PARTS.indexOf(key.replace(/_([a-z])/g, (_, c) => c.toUpperCase()));
		const textureID = WOUND_TEXTURES.indexOf(texture);
		if (part < 0 || textureID < 0 || ![x, y, size, rotation, burn].every(Number.isFinite)) return;
		this.adopt(character);
		this.tokens = Math.min(WOUND_LIMITS.batch, this.tokens + Math.max(0, now - this.time) * WOUND_LIMITS.perSecond / 1000);
		this.time = now;
		if (this.tokens < 1 || this.pending.length >= WOUND_LIMITS.batch) return;
		this.tokens--;
		const source = body.myTexture.originalSprite.texture;
		const wound = {id: ++this.sequence, part,
			x: Math.round(clamp(x * 4, -4096, 4096)), y: Math.round(clamp(y * 4, -4096, 4096)),
			size: Math.round(clamp(size * 32, 1, 255)), rotation: angleByte(rotation),
			direction: angleByte(Math.atan2(y - source.height / 2, x - source.width / 2)),
			texture: textureID, flags: (this.sequence <= WOUND_LIMITS.history ? WOUND_FLAGS.DECAL : 0) | (carving ? WOUND_FLAGS.CARVE : 0) | WOUND_FLAGS.SPRAY | (longSpray ? WOUND_FLAGS.LONG_SPRAY : 0),
			burn: Math.round(clamp(burn * 255, 0, 255)),
		};
		if (wound.flags & WOUND_FLAGS.DECAL) this.history.push(wound);
		this.pending.push(wound);
	}
	flush(character, send) {
		this.adopt(character);
		if (!this.character) return;
		const epoch = character.multiplayerPoseEpoch || 0;
		if (this.sentEpoch === epoch && !this.pending.length) return;
		send(woundModel.toBuffer({generation: this.generation, epoch, mode: WOUND_MODE.LIVE, wounds: this.pending}));
		this.pending = [];
		this.sentEpoch = epoch;
	}
	snapshot(character, send) {
		this.adopt(character);
		if (!this.character) return;
		for (let i = 0; i < Math.max(1, this.history.length); i += WOUND_LIMITS.batch) {
			send(woundModel.toBuffer({generation: this.generation, epoch: character.multiplayerPoseEpoch || 0, mode: WOUND_MODE.HISTORY, wounds: this.history.slice(i, i + WOUND_LIMITS.batch)}));
		}
	}
}

export class RemoteWounds {
	constructor() { this.generation = 0; this.clear(); }
	clear() { this.epoch = -1; this.stamps = []; this.sprays = []; this.ids = new Set(); this.latestID = 0; }
	receive(packet, now = performance.now()) {
		if (packet.generation < this.generation) return false;
		const reset = packet.generation > this.generation;
		if (reset) { this.clear(); this.generation = packet.generation; }
		this.epoch = packet.epoch;
		for (const wound of packet.wounds) {
			const fresh = wound.id > this.latestID;
			this.latestID = Math.max(this.latestID, wound.id);
			if ((wound.flags & WOUND_FLAGS.DECAL) && !this.ids.has(wound.id)) {
				this.ids.add(wound.id);
				this.stamps.push(wound);
			}
			if (fresh && packet.mode === WOUND_MODE.LIVE && (wound.flags & WOUND_FLAGS.SPRAY)) {
				if (this.sprays.length >= WOUND_LIMITS.pendingSprays) this.sprays.shift();
				this.sprays.push({wound, time: now});
			}
		}
		return reset;
	}
}

export const localWounds = new LocalWounds();
